import React from 'react';
import { cssMap } from '@compiled/react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { SnippetPreview } from '../../preview/SnippetPreview.tsx';
import type { Snippet } from '../../types/snippet.ts';

const PREVIEW_WIDTH_VW = '40vw';
const PREVIEW_MAX_WIDTH_PX = 1000;
const PREVIEW_MIN_WIDTH_PX = 480;
const PREVIEW_MAX_HEIGHT_VW = '60vh';
const PREVIEW_PADDING = token('space.200');

const styles = cssMap({
	container: {
		width: PREVIEW_WIDTH_VW,
		maxWidth: `${PREVIEW_MAX_WIDTH_PX}px`,
		minWidth: `${PREVIEW_MIN_WIDTH_PX}px`,
		padding: PREVIEW_PADDING,

		maxHeight: PREVIEW_MAX_HEIGHT_VW,
		minHeight: '200px',

		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),

		display: 'flex',
		flexDirection: 'column',

		// Panel Width (400) + DISTANCE_FROM_ITEM (32) + PREVIEW_MIN_WIDTH_PX (480) = 812px
		'@media screen and (max-width: 812px)': {
			display: 'none',
		},
	},
	header: {
		paddingBottom: token('space.150'),
		paddingInline: token('space.050'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	previewWrapper: {
		display: 'flex',
		justifyContent: 'center',
		flexGrow: 1,
		flexShrink: 1,
		overflow: 'hidden',
		height: 'calc(100% - 85px)',
		paddingBlock: token('space.100'),
	},
	// Workaround for header not allowing truncation
	name: {
		font: token('font.heading.xsmall'),
		fontWeight: token('font.weight.medium'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: '3',
	},
});

const SnippetInfo = ({ snippet }: { snippet: Snippet }) => (
	<div data-testid="snippet-info" css={styles.header}>
		<Stack space="space.100">
			<Box as="h2" xcss={styles.name}>
				{snippet.name}
			</Box>
			{snippet.description && (
				<Text color="color.text.subtle" size="small" maxLines={2}>
					{snippet.description}
				</Text>
			)}
		</Stack>
	</div>
);

export const SnippetPopupPreviewFrame = ({ snippet }: { snippet?: Snippet }): React.JSX.Element => {
	const widthBeforeMinimum = `min(${PREVIEW_WIDTH_VW}, ${PREVIEW_MAX_WIDTH_PX}px)`;
	const previewWrapperWidth = `max(${widthBeforeMinimum}, ${PREVIEW_MIN_WIDTH_PX}px)`;
	return (
		// NOTE: Cannot use Box or other primitives due to the usage of @media in the styles
		<div css={styles.container}>
			{snippet && (
				<>
					<SnippetInfo snippet={snippet} />
					<div css={styles.previewWrapper}>
						<SnippetPreview
							snippet={snippet}
							dimensions={{
								width: previewWrapperWidth,
								maxHeight: PREVIEW_MAX_HEIGHT_VW,
							}}
							renderWidth={800}
							loadingComponent={<Spinner size="large" />}
						/>
					</div>
				</>
			)}
		</div>
	);
};
